import React from 'react';
import CoreComponent from '../../../services/engines/CoreComponent.js';
import { handleDragMouseDownHelper, onDragMouseMoveHelper, onDragMouseUpHelper } from './UmlShapeWidgetBasehandleDragMouseDownHelper.jsx';
import { handleResizeMouseDownHelper, CURSORS } from './UmlShapeWidgetBasehandleResizeMouseDownHelper.jsx';
import './UmlShapeWidgetBase.css';

const HANDLES = ['nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'];

class UmlShapeWidgetBase extends CoreComponent {
    _name = 'UmlShapeWidgetBase';
    state = { editing: false, draftValue: '' };

    /** @returns {string[]} disabled resize handle keys */
    getDisabledHandles() {
        return [];
    }

    /** @returns {{width: number, height: number}} content-aware minimum size, override per widget */
    getMinSize() {
        return { width: 120, height: 60 };
    }

    /** @returns {string|null} CSS class of the clickable editable zone, null to opt out */
    getEditableZoneClassName() {
        return null;
    }

    /** @returns {string|null} CSS class for the edit input/textarea */
    getEditableInputClassName() {
        return null;
    }

    /** @returns {string} current text shown when entering edit mode */
    getEditableText() {
        return '';
    }

    /** @returns {'input'|'textarea'} field type for the editable zone */
    getEditableFieldType() {
        return 'input';
    }

    /**
     * Click-vs-drag resolution callback, invoked from onDragMouseUpHelper.
     * Selects the shape and opens inline edit in one step.
     * @param {EventTarget} target - mousedown target
     */
    handlePlainClick = (target) => {
        const zoneClassName = this.getEditableZoneClassName();
        if (!zoneClassName || !target || !target.closest(`.${zoneClassName}`)) {
            return;
        }

        this.logDebug('handlePlainClick', `select + edit shapeId=${this.props.shape.id}`);
        if (this.props.onShapeSelected) {
            this.props.onShapeSelected(this.props.shape.id);
        }
        this.setState({ editing: true, draftValue: this.getEditableText() });
    };

    handleEditChange = (e) => {
        this.setState({ draftValue: e.target.value });
    };

    /** Commits the draft value via onShapeRenamed, ignores empty values */
    commitEdit = () => {
        const value = this.state.draftValue.trim();
        if (value && this.props.onShapeRenamed) {
            this.props.onShapeRenamed(this.props.shape.id, value);
        }
        this.setState({ editing: false });
    };

    /**
     * Enter commits (single-line) or Ctrl/Cmd+Enter commits (multiline,
     * plain Enter inserts a newline instead). Escape cancels.
     * @param {KeyboardEvent} e
     */
    handleEditKeyDown = (e) => {
        const isMultiline = this.getEditableFieldType() === 'textarea';
        if (e.key === 'Enter') {
            if (!isMultiline || e.ctrlKey || e.metaKey) {
                e.preventDefault();
                this.commitEdit();
            }
        } else if (e.key === 'Escape') {
            this.setState({ editing: false });
            e.preventDefault();
            e.stopPropagation();
        }
    };

    /**
     * Renders the read-only zone, or an input/textarea while editing.
     * Uses React.createElement (this file is .js, not .jsx).
     * @param {*} content
     * @returns {React.ReactElement}
     */
    renderEditableZone(content) {
        if (this.state.editing) {
            return React.createElement(this.getEditableFieldType(), {
                className: this.getEditableInputClassName(),
                value: this.state.draftValue,
                autoFocus: true,
                onMouseDown: (e) => e.stopPropagation(),
                onChange: this.handleEditChange,
                onKeyDown: this.handleEditKeyDown,
                onBlur: this.commitEdit,
            });
        }
        return React.createElement('div', { className: this.getEditableZoneClassName() }, content);
    }

    handleDragMouseDown = (e) => handleDragMouseDownHelper(this, e);
    _onDragMouseMove = (ev) => onDragMouseMoveHelper(this, ev);
    _onDragMouseUp = () => onDragMouseUpHelper(this);

    /**
     * @param {string} handle - one of HANDLES
     * @param {MouseEvent} e
     */
    handleResizeMouseDown = (handle, e) => handleResizeMouseDownHelper(this, handle, e);

    getResizeHandleProps(handle) {
        return {
            onMouseDown: (e) => this.handleResizeMouseDown(handle, e),
            style: { cursor: CURSORS[handle] },
        };
    }

    renderResizeHandles() {
        const disabled = new Set(this.getDisabledHandles());
        return HANDLES.filter(function keepEnabled(h) {
            return !disabled.has(h);
        }).map((handle) =>
            React.createElement('div', {
                key: handle,
                className: `uml-resize-handle uml-resize-${handle}`,
                ...this.getResizeHandleProps(handle),
            }),
        );
    }
}

export default UmlShapeWidgetBase;
